import { imageElementToBase64DataUrl } from '@/lib/a11y-image-base64';

const SPEAK_ENDPOINT = '/api/tts/speak-selection';
const DESCRIBE_AUDIO_ENDPOINT = '/api/a11y/image-audio/describe-audio';
const MIN_IMAGE_SIZE_PX = 28;

export function readImageHoverAudioEnabled(): boolean {
  try {
    const raw = localStorage.getItem('lyrra-a11y-v3');
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { autoPlayImageDescriptionOnHover?: boolean };
    return Boolean(parsed.autoPlayImageDescriptionOnHover);
  } catch {
    return false;
  }
}

export function findImageFromComposedPath(event: Event): HTMLImageElement | null {
  for (const node of event.composedPath()) {
    if (node instanceof HTMLImageElement) return node;
  }
  return null;
}

function isHiddenOrTiny(img: HTMLImageElement): boolean {
  if (img.getAttribute('aria-hidden') === 'true') return true;
  if (img.getAttribute('role') === 'presentation') return true;
  if (img.closest('[data-a11y-no-speak]')) return true;
  if (img.closest('[data-a11y-image-hover-managed]')) return true;

  const rect = img.getBoundingClientRect();
  if (rect.width > 0 && rect.height > 0 && (rect.width < MIN_IMAGE_SIZE_PX || rect.height < MIN_IMAGE_SIZE_PX)) {
    return true;
  }

  const style = window.getComputedStyle(img);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return true;

  return false;
}

export function shouldSkipGlobalImageHover(img: HTMLImageElement): boolean {
  if (isHiddenOrTiny(img)) return true;
  const src = img.currentSrc || img.src || '';
  if (!src.trim()) return true;
  return false;
}

export function resolveImageAbsoluteUrl(img: HTMLImageElement): string {
  const raw = (img.currentSrc || img.src || '').trim();
  if (!raw) return '';
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('data:')) return raw;
  if (raw.startsWith('/')) return `${window.location.origin}${raw}`;
  try {
    return new URL(raw, window.location.href).href;
  } catch {
    return raw;
  }
}

export function readPreGeneratedDescriptionAudioUrl(img: HTMLImageElement): string | null {
  const direct =
    img.getAttribute('data-a11y-description-audio') ??
    img.getAttribute('data-description-audio-url');
  if (direct?.trim()) return direct.trim();

  const parent = img.closest('[data-a11y-description-audio], [data-description-audio-url]');
  const fromParent =
    parent?.getAttribute('data-a11y-description-audio') ??
    parent?.getAttribute('data-description-audio-url');
  return fromParent?.trim() || null;
}

export function extractImageDescriptionText(img: HTMLImageElement): string | null {
  const title = img.getAttribute('title')?.trim();
  if (title) return title;

  const alt = img.getAttribute('alt')?.trim();
  if (alt) return alt;

  const ariaLabel = img.getAttribute('aria-label')?.trim();
  if (ariaLabel) return ariaLabel;

  const figcaption = img.closest('figure')?.querySelector('figcaption')?.textContent?.trim();
  if (figcaption) return figcaption;

  const describedBy = img.getAttribute('aria-describedby');
  if (describedBy) {
    const root = img.getRootNode();
    const parts = describedBy
      .split(/\s+/)
      .map((id) => {
        if (root instanceof Document || root instanceof ShadowRoot) {
          return root.getElementById(id)?.textContent?.trim() ?? '';
        }
        return document.getElementById(id)?.textContent?.trim() ?? '';
      })
      .filter(Boolean);
    if (parts.length > 0) return parts.join('. ');
  }

  return null;
}

export type ImageHoverLoadingKind = 'tts' | 'vision' | 'url' | 'none';

/** Indique le type de génération attendu (pour le libellé de l’indicateur). */
export function resolveImageHoverLoadingKind(img: HTMLImageElement): ImageHoverLoadingKind {
  if (shouldSkipGlobalImageHover(img)) return 'none';
  if (readPreGeneratedDescriptionAudioUrl(img)) return 'url';
  return 'vision';
}

export function getImageBoundingRect(img: HTMLImageElement): DOMRect {
  return img.getBoundingClientRect();
}

export type ImageHoverAudioCacheEntry = {
  objectUrl: string;
  source: 'url' | 'tts' | 'vision';
};

const sessionCache = new Map<string, ImageHoverAudioCacheEntry>();
const inflight = new Map<string, Promise<ImageHoverAudioCacheEntry | null>>();

function cacheKey(kind: string, value: string, language: string): string {
  return `${kind}|${language}|${value}`;
}

function blobToObjectUrl(blob: Blob): ImageHoverAudioCacheEntry {
  return { objectUrl: URL.createObjectURL(blob), source: 'tts' };
}

export async function fetchImageHoverAudio(
  img: HTMLImageElement,
  language: string,
): Promise<ImageHoverAudioCacheEntry | null> {
  const preGenerated = readPreGeneratedDescriptionAudioUrl(img);
  if (preGenerated) {
    const resolved = preGenerated.startsWith('/')
      ? `${window.location.origin}${preGenerated}`
      : preGenerated;
    const key = cacheKey('url', resolved, language);
    const cached = sessionCache.get(key);
    if (cached) return cached;
    const entry: ImageHoverAudioCacheEntry = { objectUrl: resolved, source: 'url' };
    sessionCache.set(key, entry);
    return entry;
  }

  const imageSrcKey = `${resolveImageAbsoluteUrl(img)}|${img.naturalWidth}x${img.naturalHeight}`;
  const visionCacheKey = cacheKey('vision', imageSrcKey, language);
  const cachedVision = sessionCache.get(visionCacheKey);
  if (cachedVision) return cachedVision;
  const pendingVision = inflight.get(visionCacheKey);
  if (pendingVision) return pendingVision;

  const visionPromise = (async () => {
    try {
      const imageBase64 = await imageElementToBase64DataUrl(img, resolveImageAbsoluteUrl);
      if (!imageBase64) return null;

      const response = await fetch(DESCRIBE_AUDIO_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'audio/mpeg' },
        body: JSON.stringify({ imageBase64, language }),
        cache: 'no-store',
        credentials: 'same-origin',
      });
      if (!response.ok) return null;
      const blob = await response.blob();
      const entry: ImageHoverAudioCacheEntry = {
        objectUrl: URL.createObjectURL(blob),
        source: 'vision',
      };
      sessionCache.set(visionCacheKey, entry);
      return entry;
    } finally {
      inflight.delete(visionCacheKey);
    }
  })();
  inflight.set(visionCacheKey, visionPromise);

  const visionResult = await visionPromise;
  if (visionResult) return visionResult;

  const text = extractImageDescriptionText(img);
  if (!text) return null;

  const key = cacheKey('tts', text, language);
  const cached = sessionCache.get(key);
  if (cached) return cached;
  const pending = inflight.get(key);
  if (pending) return pending;

  const promise = (async () => {
    try {
      const response = await fetch(SPEAK_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'audio/mpeg' },
        body: JSON.stringify({ text: text.slice(0, 1500), language }),
        cache: 'no-store',
        credentials: 'same-origin',
      });
      if (!response.ok) return null;
      const blob = await response.blob();
      const entry = blobToObjectUrl(blob);
      sessionCache.set(key, entry);
      return entry;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, promise);
  return promise;
}

export function bindGlobalPointerHover(
  enabled: () => boolean,
  onEnter: (img: HTMLImageElement) => void,
  onLeave: (img: HTMLImageElement) => void,
): () => void {
  let activeImg: HTMLImageElement | null = null;

  const handleOver = (event: PointerEvent) => {
    if (!enabled()) return;
    const img = findImageFromComposedPath(event);
    if (!img || img === activeImg) return;
    if (shouldSkipGlobalImageHover(img)) return;
    activeImg = img;
    onEnter(img);
  };

  const handleOut = (event: PointerEvent) => {
    if (!activeImg) return;
    const related = event.relatedTarget;
    if (related instanceof Node && (activeImg === related || activeImg.contains(related))) return;
    const leaving = activeImg;
    activeImg = null;
    onLeave(leaving);
  };

  const handleFocusIn = (event: FocusEvent) => {
    if (!enabled()) return;
    const img = findImageFromComposedPath(event);
    if (!img || img === activeImg) return;
    if (shouldSkipGlobalImageHover(img)) return;
    activeImg = img;
    onEnter(img);
  };

  const handleFocusOut = (event: FocusEvent) => {
    if (!activeImg) return;
    const related = event.relatedTarget;
    if (related instanceof Node && activeImg.contains(related)) return;
    const leaving = activeImg;
    activeImg = null;
    onLeave(leaving);
  };

  document.addEventListener('pointerover', handleOver, true);
  document.addEventListener('pointerout', handleOut, true);
  document.addEventListener('focusin', handleFocusIn, true);
  document.addEventListener('focusout', handleFocusOut, true);

  return () => {
    document.removeEventListener('pointerover', handleOver, true);
    document.removeEventListener('pointerout', handleOut, true);
    document.removeEventListener('focusin', handleFocusIn, true);
    document.removeEventListener('focusout', handleFocusOut, true);
    activeImg = null;
  };
}

